/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CONTRACT_STATUS_LABEL } from '@kd/shared';
import { DocumentsTable } from '@/components/DocumentsTable';
import { TasksTable } from '@/components/TasksTable';
import { Badge, Button, Empty, ErrorText, Field, Input, KV, Modal, PageHeader, Select, Spinner, Tabs } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtMoney, today } from '@/lib/format';
import { ContractForm, statusTone } from './Contracts';

type Tab = 'stages' | 'payments' | 'tasks' | 'documents';
const NEXT: Record<string, { to: string; label: string }[]> = {
  DRAFT: [{ to: 'APPROVAL', label: 'На согласование' }, { to: 'ACTIVE', label: 'Подписан' }],
  APPROVAL: [{ to: 'ACTIVE', label: 'Подписан' }, { to: 'DRAFT', label: 'Вернуть в черновик' }],
  ACTIVE: [{ to: 'CLOSED', label: 'Исполнен' }, { to: 'TERMINATED', label: 'Расторгнуть' }],
  CLOSED: [{ to: 'ACTIVE', label: 'Возобновить' }], TERMINATED: [],
};

export function ContractCardPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('stages');
  const [editing, setEditing] = useState(false);
  const { data: c, isLoading, error } = useQuery({ queryKey: ['contract', id], queryFn: () => api.get(`/contracts/${id}`) });
  const refresh = () => qc.invalidateQueries({ queryKey: ['contract', id] });
  const status = useMutation({ mutationFn: (s: string) => api.patch(`/contracts/${id}`, { status: s }), onSuccess: refresh });
  if (isLoading) return <Spinner />;
  if (error || !c) return <ErrorText error={error} />;
  const fin = c.finance;
  return (
    <>
      <PageHeader title={`Договор № ${c.number} от ${fmtDate(c.date)}`}
        subtitle={<span><Link className="text-brand-700 hover:underline" to={`/parties/${c.partyId}`}>{c.party.name}</Link> · <Badge tone={statusTone(c.status)}>{CONTRACT_STATUS_LABEL[c.status]}</Badge></span>}
        actions={can('contract.write') && <>
          {NEXT[c.status].map((n) => <Button key={n.to} variant="secondary" onClick={() => status.mutate(n.to)}>{n.label}</Button>)}
          <Button variant="secondary" onClick={() => setEditing(true)}>Изменить</Button>
        </>} />
      <ErrorText error={status.error} />
      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {[['Сумма договора', c.amount], ['По этапам', fin.planned], ['Сдано по актам', fin.acted], ['Оплачено', c.kind === 'SUPPLIER' ? fin.paid_out : fin.paid_in], ['Задолженность', fin.debt]].map(([l, v]) => (
          <div key={l} className="card p-3"><div className="text-xs text-slate-500">{l}</div><div className={`mt-1 text-lg font-semibold tabular-nums ${l === 'Задолженность' && Number(v) > 0 ? 'text-red-600' : ''}`}>{fmtMoney(v)}</div></div>
        ))}
      </div>
      <div className="card mb-5 p-4"><KV items={[['Предмет', c.subject], ['Срок действия', c.startDate && c.endDate ? `${fmtDate(c.startDate)} - ${fmtDate(c.endDate)}` : c.endDate ? `до ${fmtDate(c.endDate)}` : c.startDate ? `с ${fmtDate(c.startDate)}` : null],
        ['Автопролонгация', c.autoRenew ? 'да' : 'нет'], ['Затраты по договору', fmtMoney(fin.expenses)]]} /></div>
      <Tabs<Tab> value={tab} onChange={setTab} tabs={[{ id: 'stages', label: 'Этапы и акты', count: c.stages.length }, { id: 'payments', label: 'Платежи', count: c.payments.length },
        { id: 'tasks', label: 'Задачи' }, { id: 'documents', label: 'Документы' }]} />
      {tab === 'stages' && <Stages c={c} canWrite={can('contract.write')} onSaved={refresh} />}
      {tab === 'payments' && <Payments c={c} canWrite={can('contract.finance')} onSaved={refresh} />}
      {tab === 'tasks' && <TasksTable filter={{ contractId: id }} defaults={{ contractId: id, partyId: c.partyId }} />}
      {tab === 'documents' && <DocumentsTable filter={{ contractId: id, partyId: c.partyId }} />}
      <Modal title="Изменение договора" open={editing} onClose={() => setEditing(false)} wide>
        <ContractForm contract={c} onSaved={() => setEditing(false)} />
      </Modal>
    </>
  );
}

function Stages({ c, canWrite, onSaved }: { c: any; canWrite: boolean; onSaved: () => void }) {
  const [rows, setRows] = useState<any[]>(c.stages.map((s: any) => ({ ...s })));
  const [dirty, setDirty] = useState(false);
  const save = useMutation({
    mutationFn: () => api.put(`/contracts/${c.id}/stages`, rows.map((r) => ({ name: r.name, amount: r.amount || null, dueDate: r.dueDate || null, actNumber: r.actNumber || null, actDate: r.actDate || null }))),
    onSuccess: () => { setDirty(false); onSaved(); },
  });
  const upd = (i: number, k: string, v: string) => { setRows(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r))); setDirty(true); };
  return (
    <div className="card overflow-x-auto">
      <table className="w-full">
        <thead className="bg-slate-50"><tr><th className="th">Этап</th><th className="th">Сумма, ₽</th><th className="th">Срок</th><th className="th">Акт №</th><th className="th">Дата акта</th><th className="th"></th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100">
              <td className="td min-w-[220px]"><Input disabled={!canWrite} value={r.name} onChange={(e) => upd(i, 'name', e.target.value)} /></td>
              <td className="td w-36"><Input disabled={!canWrite} inputMode="decimal" value={r.amount ?? ''} onChange={(e) => upd(i, 'amount', e.target.value.replace(',', '.'))} /></td>
              <td className="td w-40"><Input disabled={!canWrite} type="date" value={r.dueDate ?? ''} onChange={(e) => upd(i, 'dueDate', e.target.value)} /></td>
              <td className="td w-28"><Input disabled={!canWrite} value={r.actNumber ?? ''} onChange={(e) => upd(i, 'actNumber', e.target.value)} /></td>
              <td className="td w-40"><Input disabled={!canWrite} type="date" value={r.actDate ?? ''} onChange={(e) => upd(i, 'actDate', e.target.value)} /></td>
              <td className="td">{canWrite && <button onClick={() => { setRows(rows.filter((_, j) => j !== i)); setDirty(true); }} className="text-slate-400 hover:text-red-600" aria-label="Удалить"><Trash2 size={14} /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <Empty>Этапы не заданы</Empty>}
      {canWrite && (
        <div className="flex justify-between border-t border-slate-200 p-3">
          <Button variant="secondary" size="sm" onClick={() => { setRows([...rows, { name: '', amount: '' }]); setDirty(true); }}><Plus size={14} /> Этап</Button>
          <div className="flex items-center gap-3"><ErrorText error={save.error} /><Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>Сохранить этапы</Button></div>
        </div>
      )}
    </div>
  );
}

function Payments({ c, canWrite, onSaved }: { c: any; canWrite: boolean; onSaved: () => void }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ direction: c.kind === 'SUPPLIER' ? 'OUT' : 'IN', amount: '', plannedDate: '', paidDate: today(), purpose: '' });
  const add = useMutation({
    mutationFn: () => api.post(`/contracts/${c.id}/payments`, { ...f, plannedDate: f.plannedDate || null, paidDate: f.paidDate || null, purpose: f.purpose || null }),
    onSuccess: () => { setAdding(false); onSaved(); },
  });
  const mark = useMutation({ mutationFn: (pid: string) => api.patch(`/contracts/${c.id}/payments/${pid}`, { paidDate: today() }), onSuccess: onSaved });
  const del = useMutation({ mutationFn: (pid: string) => api.del(`/contracts/${c.id}/payments/${pid}`), onSuccess: onSaved });
  return (
    <div className="card">
      {canWrite && <div className="flex justify-end border-b border-slate-200 p-2"><Button size="sm" onClick={() => setAdding(true)}>Платёж</Button></div>}
      {!c.payments.length ? <Empty>Платежей нет</Empty> : (
        <table className="w-full">
          <thead className="bg-slate-50"><tr><th className="th">Направление</th><th className="th text-right">Сумма</th><th className="th">План</th><th className="th">Оплачен</th><th className="th">Назначение</th><th className="th"></th></tr></thead>
          <tbody>{c.payments.map((p: any) => (
            <tr key={p.id} className="border-t border-slate-100">
              <td className="td">{p.direction === 'IN' ? 'Поступление' : 'Оплата'}</td><td className="td text-right tabular-nums">{fmtMoney(p.amount)}</td>
              <td className="td">{fmtDate(p.plannedDate)}</td>
              <td className="td">{p.paidDate ? fmtDate(p.paidDate) : canWrite ? <Button size="sm" variant="secondary" onClick={() => mark.mutate(p.id)}>Отметить оплату</Button> : <Badge tone="amber">ожидается</Badge>}</td>
              <td className="td">{p.purpose}</td>
              <td className="td">{canWrite && <button className="text-slate-400 hover:text-red-600" onClick={() => confirm('Удалить платёж?') && del.mutate(p.id)} aria-label="Удалить"><Trash2 size={14} /></button>}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
      <Modal title="Платёж" open={adding} onClose={() => setAdding(false)}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Направление"><Select value={f.direction} onChange={(e) => setF({ ...f, direction: e.target.value })} options={[{ value: 'IN', label: 'Поступление' }, { value: 'OUT', label: 'Оплата' }]} /></Field>
            <Field label="Сумма, ₽"><Input inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value.replace(',', '.') })} /></Field>
            <Field label="Плановая дата"><Input type="date" value={f.plannedDate} onChange={(e) => setF({ ...f, plannedDate: e.target.value })} /></Field>
            <Field label="Дата оплаты" hint="Пусто - платёж ожидается"><Input type="date" value={f.paidDate} onChange={(e) => setF({ ...f, paidDate: e.target.value })} /></Field>
          </div>
          <Field label="Назначение"><Input value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} /></Field>
          <ErrorText error={add.error} />
          <div className="flex justify-end"><Button disabled={!f.amount} onClick={() => add.mutate()}>Сохранить</Button></div>
        </div>
      </Modal>
    </div>
  );
}
